/**
 * Invisible video watermark (client embedder). v3
 *
 * Payload: 32-bit session code (MSB first) + CRC-16/CCITT-FALSE = 48 bits.
 * Each 8x8 luma block carries ONE bit in the SIGN of
 *     D = DCT(5,3) - DCT(3,5)        (bit 1 -> D >= +MARGIN, bit 0 -> D <= -MARGIN)
 * Sign embedding degrades gracefully under H.264 noise; the v2 parity (QIM)
 * scheme turned into a coin flip once codec noise approached its step size.
 * The bit slot of a block depends ONLY on its block coordinates:
 *     slot = (bx + SLOT_STRIDE * by) % PAYLOAD_BITS
 * It does NOT depend on the canvas width (v2 used by*bw+bx, so the slot map
 * changed with every player size and the extractor could never line up).
 * The extractor searches grid offset, slot rotation and scale on its own.
 *
 * KEEP THESE CONSTANTS IDENTICAL IN server/utils/rightra/watermarkExtractor.js
 */

export const BLOCK = 8;
export const PAYLOAD_BITS = 48;
export const MARGIN = 16;
export const SLOT_STRIDE = 7;

const COS_5 = precompute(5);
const COS_3 = precompute(3);

function precompute(u) {
  const a = new Float32Array(8);
  for (let i = 0; i < 8; i++) a[i] = Math.cos(((2 * i + 1) * u * Math.PI) / 16);
  return a;
}

const clamp8 = (v) => (v < 0 ? 0 : v > 255 ? 255 : v);

export const slotOf = (bx, by) => (bx + SLOT_STRIDE * by) % PAYLOAD_BITS;

/** CRC-16/CCITT-FALSE (init 0xFFFF) over the 4 code bytes. Init != 0 so an all-zero payload is NOT valid. */
export function crc16(value) {
  let crc = 0xffff;
  for (let i = 0; i < 4; i++) {
    crc ^= ((value >>> (24 - i * 8)) & 0xff) << 8;
    for (let j = 0; j < 8; j++) crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
  }
  return crc;
}

export function packPayload(code) {
  const bits = new Uint8Array(PAYLOAD_BITS);
  const c = code >>> 0;
  for (let i = 0; i < 32; i++) bits[i] = (c >>> (31 - i)) & 1;
  const crc = crc16(c);
  for (let i = 0; i < 16; i++) bits[32 + i] = (crc >>> (15 - i)) & 1;
  return bits;
}

export function unpackPayload(soft) {
  let code = 0;
  for (let i = 0; i < 32; i++) code = ((code << 1) | (soft[i] > 0.5 ? 1 : 0)) >>> 0;
  let crc = 0;
  for (let i = 0; i < 16; i++) crc = ((crc << 1) | (soft[32 + i] > 0.5 ? 1 : 0)) & 0xffff;
  return { code, valid: crc === crc16(code) };
}

function readBlock(rgba, width, x0, y0) {
  const block = new Float32Array(64);
  for (let y = 0; y < 8; y++)
    for (let x = 0; x < 8; x++) {
      const o = ((y0 + y) * width + x0 + x) * 4;
      block[y * 8 + x] = 0.299 * rgba[o] + 0.587 * rgba[o + 1] + 0.114 * rgba[o + 2] - 128;
    }
  return block;
}

function diffOf(block) {
  let a = 0, b = 0;
  for (let y = 0; y < 8; y++)
    for (let x = 0; x < 8; x++) {
      const v = block[y * 8 + x];
      a += v * COS_5[x] * COS_3[y];
      b += v * COS_3[x] * COS_5[y];
    }
  return 0.25 * (a - b);
}

function embedBlock(rgba, width, x0, y0, bit) {
  const block = readBlock(rgba, width, x0, y0);
  const diff = diffOf(block);
  let target = diff;
  if (bit && diff < MARGIN) target = MARGIN;
  else if (!bit && diff > -MARGIN) target = -MARGIN;
  if (target === diff) return; // already carries the bit with enough margin
  const half = (target - diff) / 2;

  for (let y = 0; y < 8; y++)
    for (let x = 0; x < 8; x++) {
      block[y * 8 + x] += 0.25 * half * (COS_5[x] * COS_3[y] - COS_3[x] * COS_5[y]);
    }
  for (let y = 0; y < 8; y++)
    for (let x = 0; x < 8; x++) {
      const o = ((y0 + y) * width + x0 + x) * 4;
      const r = rgba[o], g = rgba[o + 1], b = rgba[o + 2];
      const dy = block[y * 8 + x] + 128 - (0.299 * r + 0.587 * g + 0.114 * b);
      rgba[o] = clamp8(r + dy);
      rgba[o + 1] = clamp8(g + dy);
      rgba[o + 2] = clamp8(b + dy);
    }
}

export function embedFrame(rgba, width, height, code) {
  const bits = packPayload(code);
  const bw = Math.floor(width / BLOCK);
  const bh = Math.floor(height / BLOCK);
  for (let by = 0; by < bh; by++)
    for (let bx = 0; bx < bw; bx++) embedBlock(rgba, width, bx * BLOCK, by * BLOCK, bits[slotOf(bx, by)]);
}

/** Aligned, unscaled extraction. Only for tests; the server extractor also searches offset/rotation/scale. */
export function extractFrame(rgba, width, height) {
  const ones = new Float32Array(PAYLOAD_BITS);
  const n = new Float32Array(PAYLOAD_BITS);
  const bw = Math.floor(width / BLOCK);
  const bh = Math.floor(height / BLOCK);
  for (let by = 0; by < bh; by++)
    for (let bx = 0; bx < bw; bx++) {
      const s = slotOf(bx, by);
      ones[s] += diffOf(readBlock(rgba, width, bx * BLOCK, by * BLOCK)) > 0 ? 1 : 0;
      n[s] += 1;
    }
  const soft = ones.map((v, i) => (n[i] ? v / n[i] : 0.5));
  return unpackPayload(soft);
}

export class VideoWatermark {
  /**
   * @param {HTMLElement} container
   * @param {HTMLVideoElement} video
   * @param {number} code 32-bit session watermark code
   * @param {{onFail?: (err: Error) => void}} [opts]
   */
  constructor(container, video, code, opts = {}) {
    this.container = container;
    this.video = video;
    this.code = code >>> 0;
    this.onFail = opts.onFail || (() => {});
    this.canvas = null;
    this.ctx = null;
    this.raf = 0;
    this.running = false;
    this._lastW = 0;
    this._lastH = 0;
  }

  attach() {
    const c = document.createElement("canvas");
    c.style.cssText =
      "position:absolute;inset:0;width:100%;height:100%;object-fit:contain;z-index:2;pointer-events:none;background:#000";
    c.setAttribute("aria-hidden", "true");
    const ctx = c.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("2D canvas is not available.");
    this.container.appendChild(c);
    this.canvas = c;
    this.ctx = ctx;
    this.video.style.setProperty("visibility", "hidden", "important");
    this.running = true;
    this._loop();
  }

  _loop = () => {
    if (!this.running) return;
    this.raf = requestAnimationFrame(this._loop);
    const v = this.video;
    if (!v.videoWidth || !v.videoHeight) return;
    // keep the raw <video> hidden even if someone un-hides it in devtools
    if (v.style.visibility !== "hidden") v.style.setProperty("visibility", "hidden", "important");
    const w = Math.min(v.videoWidth, this.container.clientWidth || v.videoWidth);
    const h = Math.round((v.videoHeight / v.videoWidth) * w);
    if (w !== this._lastW || h !== this._lastH) {
      this.canvas.width = w;
      this.canvas.height = h;
      this._lastW = w;
      this._lastH = h;
    }
    try {
      this.ctx.drawImage(v, 0, 0, w, h);
      const img = this.ctx.getImageData(0, 0, w, h);
      embedFrame(img.data, w, h, this.code);
      this.ctx.putImageData(img, 0, 0);
    } catch (e) {
      // Fail closed: never leave the viewer with an unmarked picture.
      this.running = false;
      this.onFail(e);
    }
  };

  detach() {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    if (this.canvas && this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas);
    this.canvas = null;
    this.ctx = null;
    if (this.video) this.video.style.removeProperty("visibility");
  }
}